import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  Typography,
  Button,
  IconButton,
  Chip,
  Stack,
  Divider,
  Card,
  CardMedia,
  Breadcrumbs,
  Link,
  Rating,
  TextField,
  Fade,
  CircularProgress,
} from '@mui/material';
import {
  Add,
  Remove,
  ShoppingCart,
  LocalShipping,
  VerifiedUser,
  Favorite,
  FavoriteBorder,
  Share,
  ArrowBack,
  CheckCircle,
} from '@mui/icons-material';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useCurrency } from '../context/CurrencyContext';
import { productAPI, wishlistAPI } from '../services/api';
import ProductCard from '../components/ProductCard';

const ProductDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addToCart, setIsCartOpen } = useCart();
  const { isAuthenticated } = useAuth();
  const [product, setProduct] = useState(null);
  const [variants, setVariants] = useState([]);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [wishlistLoading, setWishlistLoading] = useState(false);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const { userCountry } = useCurrency();

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchProduct();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  useEffect(() => {
    const checkWishlistStatus = async () => {
      if (product && isAuthenticated) {
        try {
          const response = await wishlistAPI.check(product.id);
          setIsWishlisted(response.isInWishlist);
        } catch (error) {
          console.log('Error checking wishlist:', error);
        }
      }
    };
    checkWishlistStatus();
  }, [product, isAuthenticated]);

  const fetchProduct = async () => {
    try {
      setLoading(true);
      const response = await productAPI.getAllProducts({ limit: 100, userCountry: userCountry });

      if (response.success && response.data) {
        const foundProduct = response.data.find((p) => p.slug === slug || p._id === slug);

        if (foundProduct) {
          const transformedProduct = {
            id: foundProduct._id,
            name: foundProduct.name,
            category: foundProduct.subCategory?.name || 'Uncategorized',
            price: foundProduct.variants?.[0]?.sellingPrice || 0,
            mrp: foundProduct.variants?.[0]?.mrp || 0,
            image: foundProduct.images?.[0],
            images: foundProduct.images || [],
            description: foundProduct.description || '',
            benefits: foundProduct.attributes?.map(attr => attr.name) || [],
            featured: true,
            slug: foundProduct.slug,
            variants: foundProduct.variants || [],
            stock: foundProduct.variants?.[0]?.stock || 0,
          };

          setProduct(transformedProduct);
          setVariants(foundProduct.variants || []);

          if (foundProduct.variants && foundProduct.variants.length > 0) {
            setSelectedVariant(foundProduct.variants[0]);
          }

          // Fetch related products
          const relatedResponse = await productAPI.getAllProducts({
            subCategory: foundProduct.subCategory?._id,
            limit: 20,
            userCountry: userCountry
          });

          if (relatedResponse.success && relatedResponse.data) {
            const related = relatedResponse.data
              .filter((p) => p._id !== foundProduct._id)
              .slice(0, 3)
              .map(p => ({
                id: p._id,
                name: p.name,
                category: p.subCategory?.name || 'Uncategorized',
                price: p.variants?.[0]?.sellingPrice || 0,
                mrp: p.variants?.[0]?.mrp || 0,
                image: p.images?.[0],
                description: p.description || '',
                benefits: p.attributes?.map(attr => attr.name) || [],
                featured: true,
                slug: p.slug,
                variants: p.variants || [],
                stock: p.variants?.[0]?.stock || 0,
              }));
            setRelatedProducts(related);
          }
        }
      }
    } catch (err) {
      console.error('Error fetching product:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleQuantityChange = (change) => {
    const newQuantity = quantity + change;
    if (newQuantity >= 1 && newQuantity <= 10) {
      setQuantity(newQuantity);
    }
  };

  const handleAddToCart = () => {
    if (!selectedVariant) {
      alert('Please select a size');
      return;
    }

    console.log(product,quantity,selectedVariant,'ssssssssssss')
    addToCart(product, quantity, selectedVariant);
    setIsCartOpen(true);
  };

  const handleBuyNow = () => {
    if (!selectedVariant) {
      alert('Please select a size');
      return;
    }
    addToCart(product, quantity, selectedVariant);
    setIsCartOpen(true);
    // Navigate to checkout
    setTimeout(() => {
      navigate('/checkout');
    }, 500);
  };

  const handleWishlistToggle = async () => {
    if (!isAuthenticated) {
      alert('Please login to add to wishlist');
      navigate('/login');
      return;
    }

    if (!product) return;

    setWishlistLoading(true);
    try {
      if (isWishlisted) {
        await wishlistAPI.remove(product.id);
        setIsWishlisted(false);
      } else {
        await wishlistAPI.add(product.id);
        setIsWishlisted(true);
      }
    } catch (error) {
      console.error('Error updating wishlist:', error);
      alert(error.message || 'Failed to update wishlist');
    } finally {
      setWishlistLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ pt: 12, pb: 8, textAlign: 'center', minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (!product) {
    return (
      <Box sx={{ pt: 12, pb: 8, textAlign: 'center' }}>
        <Container>
          <Typography variant="h4">Product not found</Typography>
          <Button onClick={() => navigate('/shop')} sx={{ mt: 2 }}>
            Back to Shop
          </Button>
        </Container>
      </Box>
    );
  }

  const currentImage = product.images[selectedImageIndex] || product.image;

  return (
    <Box sx={{ pt: { xs: 9, md: 10 }, pb: { xs: 4, md: 8 }, bgcolor: '#F8F6F1' }}>
      <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ mb: { xs: 2, md: 3 }, fontSize: { xs: '0.85rem', md: '1rem' } }}>
          <Link
            underline="hover"
            color="inherit"
            onClick={() => navigate('/')}
            sx={{ cursor: 'pointer' }}
          >
            Home
          </Link>
          <Link
            underline="hover"
            color="inherit"
            onClick={() => navigate('/shop')}
            sx={{ cursor: 'pointer' }}
          >
            Shop
          </Link>
          <Typography color="text.primary">{product.name}</Typography>
        </Breadcrumbs>

        {/* Back Button */}
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/shop')}
          sx={{
            mb: { xs: 2, md: 3 },
            color: 'text.secondary',
            fontSize: { xs: '0.9rem', md: '1rem' },
            px: { xs: 1, md: 2 }
          }}
        >
          Back to Shop
        </Button>

        <Fade in timeout={600}>
          <Grid container spacing={{ xs: 3, md: 4 }}>
            {/* Product Image */}
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  borderRadius: 2,
                  overflow: 'hidden',
                  boxShadow: '0 8px 24px rgba(201, 169, 97, 0.15)',
                  mb: 2,
                  position: 'relative',
                }}
              >
                <CardMedia
                  component="img"
                  image={currentImage}
                  alt={product.name}
                  sx={{
                    width: '100%',
                    height: { xs: 300, sm: 400, md: 500 },
                    objectFit: 'cover',
                    opacity: selectedVariant?.stock === 0 ? 0.7 : 1,
                    transition: 'opacity 0.3s ease',
                  }}
                />

                {selectedVariant?.stock === 0 && (
                  <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    pointerEvents: 'none',
                  }}
                >
                  <Box
                sx={{
                  position: 'absolute',
                  top: '45%',
                  left: '-45%',          // 🔹 more left → wider coverage
                  width: '190%',         // 🔹 ribbon width increased
                  textAlign: 'center',
                  transform: 'rotate(-30deg)',
                  bgcolor: 'rgba(255, 90, 90, 0.9)', // 🔴 brighter light red
                  py: 2,                 // 🔹 thicker ribbon
                  boxShadow: '0 6px 18px rgba(0,0,0,0.35)',
                }}
              >
                <Typography
                  variant="h6"           // 🔥 font size increased
                  sx={{
                    color: '#fff',
                    fontWeight: 900,
                    letterSpacing: '0.15em',
                    textTransform: 'uppercase',
                  }}
                >
                  Out of Stock
                </Typography>
              </Box>

  </Box>
  )}
</Card>


              {/* Image Thumbnails */}
              {product.images.length > 1 && (
                <Grid container spacing={1}>
                  {product.images.map((img, index) => (
                    <Grid item xs={3} key={index}>
                      <Card
                        onClick={() => setSelectedImageIndex(index)}
                        sx={{
                          cursor: 'pointer',
                          border: selectedImageIndex === index ? '2px solid' : '2px solid transparent',
                          borderColor: 'primary.main',
                          overflow: 'hidden',
                          '&:hover': {
                            opacity: 0.8,
                          },
                        }}
                      >
                        <CardMedia
                          component="img"
                          image={img}
                          alt={`${product.name} ${index + 1}`}
                          sx={{
                            width: '100%',
                            height: 80,
                            objectFit: 'cover',
                          }}
                        />
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}

              {/* Product Features */}
              <Stack spacing={{ xs: 1.5, md: 2 }} sx={{ mt: { xs: 2, md: 3 }, display: { xs: 'none', md: 'flex' } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LocalShipping sx={{ color: 'primary.main', fontSize: { xs: 20, md: 24 } }} />
                  <Typography variant="body2" sx={{ fontSize: { xs: '0.85rem', md: '0.875rem' } }}>Free Shipping</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <VerifiedUser sx={{ color: 'primary.main', fontSize: { xs: 20, md: 24 } }} />
                  <Typography variant="body2" sx={{ fontSize: { xs: '0.85rem', md: '0.875rem' } }}>No Preservatives</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckCircle sx={{ color: 'primary.main', fontSize: { xs: 20, md: 24 } }} />
                  <Typography variant="body2" sx={{ fontSize: { xs: '0.85rem', md: '0.875rem' } }}>Traditional Recipes</Typography>
                </Box>
              </Stack>
            </Grid>

            {/* Product Details */}
            <Grid item xs={12} md={6}>
              <Stack spacing={{ xs: 2, md: 3 }}>
                {/* Category */}
                <Chip
                  label={product.category}
                  sx={{
                    bgcolor: 'primary.main',
                    color: 'white',
                    fontWeight: 600,
                    width: 'fit-content',
                    fontSize: { xs: '0.75rem', md: '0.8125rem' },
                    height: { xs: 28, md: 32 },
                  }}
                />

                {/* Product Name */}
                <Typography
                  variant="h3"
                  sx={{
                    fontFamily: "'Cormorant Garamond', serif",
                    fontWeight: 700,
                    fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                    lineHeight: 1.2,
                  }}
                >
                  {product.name}
                </Typography>

                {/* Rating */}
                {/* <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Rating value={4.5} precision={0.5} readOnly size="small" sx={{ fontSize: { xs: '1.2rem', md: '1.5rem' } }} />
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.85rem', md: '0.875rem' } }}>
                    (128 reviews)
                  </Typography>
                </Box> */}

                {/* Price */}
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2 }}>
                  <Typography
                    variant="h4"
                    sx={{
                      fontFamily: "'Cormorant Garamond', serif",
                      fontWeight: 700,
                      color: 'primary.main',
                      fontSize: { xs: '2rem', md: '2.125rem' },
                    }}
                  >
                    {selectedVariant.currencySymbol}{selectedVariant.sellingPrice}
                    
                  </Typography>
                  {selectedVariant && selectedVariant.mrp > selectedVariant.sellingPrice && (
                    <Typography
                      variant="h6"
                      sx={{
                        textDecoration: 'line-through',
                        color: 'text.secondary',
                        fontSize: { xs: '1.2rem', md: '1.5rem' },
                      }}
                    >
                      {selectedVariant.currencySymbol}{selectedVariant.mrp}

                    </Typography>
                  )}
                  {selectedVariant.mrp && selectedVariant.mrp > selectedVariant.sellingPrice && (
                    <Chip
                      label={`${Math.round(((selectedVariant.mrp - selectedVariant.sellingPrice) / selectedVariant.mrp) * 100)}% OFF`}
                      size="small"
                      sx={{
                        fontSize: '0.6rem',
                        height: 20,
                        bgcolor: '#4caf50',
                        color: 'white',
                        fontWeight: 600,
                      }}
                    />
                  )}
                </Box>

                <Divider />

                {/* Variant Selector */}
                {variants.length > 0 && (
                  <Box>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, fontSize: { xs: '1.1rem', md: '1.25rem' } }}>
                      Select Size
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                      {variants.map((variant) => (
                        <Chip
                          key={variant._id}
                          label={`${variant.weight} - ${variant.packaging}`}
                          onClick={() => setSelectedVariant(variant)}
                          sx={{
                            bgcolor: selectedVariant?._id === variant._id ? 'primary.main' : 'background.paper',
                            color: selectedVariant?._id === variant._id ? 'white' : 'text.primary',
                            fontWeight: 600,
                            fontSize: { xs: '0.85rem', md: '0.9rem' },
                            height: { xs: 36, md: 40 },
                            cursor: 'pointer',
                            border: '2px solid',
                            borderColor: selectedVariant?._id === variant._id ? 'primary.main' : 'divider',
                            '&:hover': {
                              bgcolor: selectedVariant?._id === variant._id ? 'primary.dark' : 'action.hover',
                            },
                          }}
                        />
                      ))}
                    </Stack>
                    {selectedVariant && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontSize: { xs: '0.85rem', md: '0.875rem' } }}>
                        {selectedVariant.stock > 0
                          ? `${selectedVariant.stock} units available`
                          : 'Out of stock'}
                      </Typography>
                    )}
                  </Box>
                )}

                <Divider />

                {/* Description */}
                <Box>
                  <Typography variant="h6" className='font13' gutterBottom sx={{ fontWeight: 600, fontSize: { xs: '1.1rem', md: '1.25rem' } }}>
                    About This Product
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8, fontSize: { xs: '0.95rem', md: '1rem' } }}>
                    {product.description}
                  </Typography>
                </Box>

                {/* Benefits */}
                <Box>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, fontSize: { xs: '1.1rem', md: '1.25rem' } }}>
                    Key Benefits
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                    {product.benefits.map((benefit, index) => (
                      <Chip
                        key={index}
                        label={benefit}
                        sx={{
                          bgcolor: 'primary.light',
                          color: 'white',
                          fontWeight: 500,
                          fontSize: { xs: '0.75rem', md: '0.8125rem' },
                          height: { xs: 28, md: 32 },
                        }}
                      />
                    ))}
                  </Stack>
                </Box>

                <Divider />

                {/* Quantity Selector */}
                <Box>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, fontSize: { xs: '1rem', md: '1.125rem' } }}>
                    Quantity
                  </Typography>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        border: '2px solid',
                        borderColor: 'primary.main',
                        borderRadius: 2,
                      }}
                    >
                      <IconButton
                        onClick={() => handleQuantityChange(-1)}
                        disabled={quantity <= 1}
                        sx={{ color: 'primary.main', p: { xs: 1.5, md: 1 } }}
                      >
                        <Remove sx={{ fontSize: { xs: 24, md: 20 } }} />
                      </IconButton>
                      <Typography
                        sx={{
                          minWidth: { xs: 60, md: 50 },
                          textAlign: 'center',
                          fontWeight: 600,
                          fontSize: { xs: '1.2rem', md: '1.1rem' },
                        }}
                      >
                        {quantity}
                      </Typography>
                      <IconButton
                        onClick={() => handleQuantityChange(1)}
                        disabled={quantity >= 10}
                        sx={{ color: 'primary.main', p: { xs: 1.5, md: 1 } }}
                      >
                        <Add sx={{ fontSize: { xs: 24, md: 20 } }} />
                      </IconButton>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.85rem', md: '0.875rem' } }}>
                      (Max 10 per order)
                    </Typography>
                  </Stack>
                </Box>

                {/* Action Buttons */}
                <Stack spacing={{ xs: 1.5, md: 2 }}>
                  <Button
                    className='form-btn'
                    variant="contained"
                    size="large"
                    startIcon={<ShoppingCart sx={{ fontSize: { xs: 20, md: 24 } }} />}
                    onClick={handleAddToCart}
                    fullWidth
                    sx={{
                      py: { xs: 1.75, md: 1.5 },
                      borderRadius: 50,
                      background: 'linear-gradient(135deg, #C9A961 0%, #B89850 100%)',
                      fontWeight: 600,
                      fontSize: { xs: '1rem', md: '1.1rem' },
                      textTransform: 'none',
                      minHeight: { xs: 48, md: 'auto' },
                      '&:hover': {
                        background: 'linear-gradient(135deg, #B89850 0%, #A88740 100%)',
                        transform: 'scale(1.02)',
                      },
                    }}
                    disabled={selectedVariant?.stock === 0 ? true : false}
                  >
                    Add to Cart
                  </Button>

                  <Button
                    variant="outlined"
                    className='form-btn'
                    size="large"
                    onClick={handleBuyNow}
                    fullWidth
                    sx={{
                      py: { xs: 1.75, md: 1.5 },
                      borderRadius: 50,
                      borderWidth: 2,
                      borderColor: 'primary.main',
                      color: 'primary.main',
                      fontWeight: 600,
                      fontSize: { xs: '1rem', md: '1.1rem' },
                      textTransform: 'none',
                      minHeight: { xs: 48, md: 'auto' },
                      '&:hover': {
                        borderWidth: 2,
                        bgcolor: 'primary.main',
                        color: 'white',
                      },
                    }}
                    disabled={selectedVariant?.stock === 0 ? true : false}
                  >
                    Buy Now
                  </Button>
                </Stack>

                {/* Wishlist & Share */}
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1, sm: 2 }}>
                  <Button
                    startIcon={isWishlisted ? <Favorite /> : <FavoriteBorder />}
                    onClick={handleWishlistToggle}
                    disabled={wishlistLoading}
                    fullWidth={{ xs: true, sm: false }}
                    sx={{
                      color: isWishlisted ? 'error.main' : 'text.secondary',
                      textTransform: 'none',
                      fontSize: { xs: '0.9rem', md: '1rem' },
                      minHeight: { xs: 44, md: 'auto' },
                    }}
                  >
                    {wishlistLoading
                      ? 'Updating...'
                      : isWishlisted
                        ? 'Added to Wishlist'
                        : 'Add to Wishlist'}
                  </Button>
                  {/* <Button
                    startIcon={<Share />}
                    fullWidth={{ xs: true, sm: false }}
                    sx={{
                      color: 'text.secondary',
                      textTransform: 'none',
                      fontSize: { xs: '0.9rem', md: '1rem' },
                      minHeight: { xs: 44, md: 'auto' },
                    }}
                  >
                    Share
                  </Button> */}
                </Stack>

                {/* Mobile Product Features */}
                <Stack spacing={1.5} sx={{ mt: 2, display: { xs: 'flex', md: 'none' }, bgcolor: 'background.paper', p: 2, borderRadius: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocalShipping sx={{ color: 'primary.main', fontSize: 20 }} />
                    <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>Free shipping on orders over $50</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <VerifiedUser sx={{ color: 'primary.main', fontSize: 20 }} />
                    <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>100% Organic & Certified</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircle sx={{ color: 'primary.main', fontSize: 20 }} />
                    <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>30-day money-back guarantee</Typography>
                  </Box>
                </Stack>
              </Stack>
            </Grid>
          </Grid>
        </Fade>

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <Box sx={{ mt: { xs: 6, md: 8 } }}>
            <Typography
              variant="h4"
              sx={{
                fontFamily: "'Cormorant Garamond', serif",
                fontWeight: 700,
                mb: { xs: 3, md: 4 },
                textAlign: 'center',
                fontSize: { xs: '1.75rem', md: '2.125rem' },
              }}
            >
              You May Also Like
            </Typography>
            <Grid container spacing={{ xs: 2, sm: 3, md: 4 }}>
              {relatedProducts.map((relatedProduct) => (
                <Grid item xs={12} sm={6} md={4} key={relatedProduct.id}>
                  <ProductCard product={relatedProduct} />
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default ProductDetailPage;
